import { useEffect, useRef, useState } from "react"
import { Link } from "react-router"
import { ArrowUpRight } from "./layout"

export function Eyebrow({ children, dot = false }: { children: React.ReactNode; dot?: boolean }) {
  return (
    <span className="inline-flex w-fit items-center gap-2.5 rounded-full border border-ink/10 bg-cream-200 px-2.5 py-1 text-sm font-medium uppercase leading-[17.5px] text-forest-700">
      {dot && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-forest-700" aria-hidden />}
      {children}
    </span>
  )
}

/** Centered hero used at the top of every inner page. */
export function PageHero({
  eyebrow,
  title,
  lede,
  eyebrowDot = false,
  className = "",
}: {
  eyebrow: React.ReactNode
  title: React.ReactNode
  lede: React.ReactNode
  eyebrowDot?: boolean
  className?: string
}) {
  return (
    <section className={`bg-cream-50 pt-10 lg:mt-[60px] lg:pt-0 ${className}`}>
      <div className="public-container text-center">
        <Eyebrow dot={eyebrowDot}>{eyebrow}</Eyebrow>
        <h1 className="mx-auto mt-6 max-w-3xl font-display text-[30px] font-bold leading-[1.1] text-ink sm:text-[40px] lg:mt-[30px] lg:max-w-none lg:text-[48px] lg:leading-none">
          {title}
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-[22px] text-ink/80 lg:mt-2.5 lg:max-w-none lg:text-lg lg:leading-[22.5px]">{lede}</p>
      </div>
    </section>
  )
}

/** Small caps label above a section heading. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-wide text-forest-700">{children}</p>
  )
}

export function PrimaryLink({ to, children, className = "" }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-3 rounded-full bg-forest-950 py-[5px] pl-5 pr-1.5 text-lg font-semibold text-white transition-colors hover:bg-forest-800 ${className}`}
    >
      {children}
      <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-forest-950">
        <ArrowUpRight />
      </span>
    </Link>
  )
}

export function GoldPill({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-3 rounded-full bg-gold-500 py-[5px] pl-5 pr-1.5 text-lg font-semibold text-ink transition-colors hover:bg-gold-400"
    >
      {children}
      <span className="grid h-10 w-10 place-items-center rounded-full bg-forest-950 text-white">
        <ArrowUpRight />
      </span>
    </Link>
  )
}

export function OutlineLink({
  to,
  light = false,
  children,
}: {
  to: string
  light?: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      to={to}
      className={
        light
          ? "rounded-full border border-cream-50/50 px-6 py-2.5 text-lg font-semibold text-white transition-colors hover:bg-cream-50 hover:text-forest-950"
          : "rounded-full border-2 border-forest-950/20 px-6 py-2.5 text-lg font-semibold text-ink transition-colors hover:border-forest-950"
      }
    >
      {children}
    </Link>
  )
}

export function WhitePill({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-full bg-white px-6 py-2.5 text-lg font-semibold text-ink transition-colors hover:bg-cream-100"
    >
      {children}
    </Link>
  )
}

/* ---------- forms ---------- */

export const publicInputClasses =
  "w-full rounded border border-gold-500 bg-cream-100 px-2.5 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-ink/40 focus:outline-none focus:ring-0 disabled:opacity-60"

export function Input({ className = "", ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${publicInputClasses} ${className}`} {...props} />
}

export function Field({
  label,
  wrapperClassName = "",
  ...props
}: { label: React.ReactNode; wrapperClassName?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={`block ${wrapperClassName}`}>
      <span className="mb-1.5 block text-base font-medium text-ink sm:mb-2 sm:text-lg">{label}</span>
      <Input {...props} />
    </label>
  )
}

export function TextAreaField({
  label,
  className = "",
  ...props
}: { label: React.ReactNode } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-base font-medium text-ink sm:mb-2 sm:text-lg">{label}</span>
      <textarea className={`${publicInputClasses} min-h-24 resize-y sm:min-h-28 ${className}`} {...props} />
    </label>
  )
}

export function CustomSelectField({
  label,
  name,
  options,
  placeholder,
  value,
  onChange,
  disabled = false,
  required = false,
}: {
  label: React.ReactNode
  name: string
  options: readonly string[]
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  required?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [uncontrolledSelected, setUncontrolledSelected] = useState(placeholder ? "" : options[0] ?? "")
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonId = `${name}-select`
  const listboxId = `${name}-options`
  const selected = value ?? uncontrolledSelected
  const selectedIndex = Math.max(0, options.indexOf(selected))

  useEffect(() => {
    if (!open) return

    function handleOutsidePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown)
  }, [open])

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return

    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      setActiveIndex(selectedIndex)
      setOpen(true)
      return
    }

    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      const direction = event.key === "ArrowDown" ? 1 : -1
      setActiveIndex((index) => Math.min(options.length - 1, Math.max(0, index + direction)))
      return
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      setOpen(true)
      setActiveIndex(event.key === "Home" ? 0 : Math.max(0, options.length - 1))
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setOpen(false)
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (open) {
        const option = options[activeIndex] ?? selected
        setUncontrolledSelected(option)
        onChange?.(option)
        setOpen(false)
      } else {
        setActiveIndex(selectedIndex)
        setOpen(true)
      }
    }
  }

  return (
    <div ref={containerRef} className="custom-select-field relative">
      <span className="mb-1.5 block text-base font-medium text-ink sm:mb-2 sm:text-lg">{label}</span>
      <input type="hidden" name={name} value={selected} required={required} />
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        disabled={disabled}
        aria-label={`${label}: ${selected || placeholder || "Select an option"}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => {
          setActiveIndex(selectedIndex)
          setOpen((isOpen) => !isOpen)
        }}
        onKeyDown={handleKeyDown}
        className="flex w-full items-center justify-between rounded border border-gold-500 bg-cream-100 px-2.5 py-2.5 text-left text-sm text-ink transition-colors hover:border-ink/30 focus:border-ink/40 focus:outline-none focus:ring-0 disabled:opacity-60"
      >
        <span className={selected ? "" : "text-ink/40"}>{selected || placeholder || "Select an option"}</span>
        <svg className={`h-4 w-4 text-ink/50 transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`} viewBox="0 0 12 8" fill="none" aria-hidden>
          <path d="m1 1.5 5 5 5-5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div
        id={listboxId}
        role="listbox"
        aria-labelledby={buttonId}
        aria-hidden={!open}
        className={`absolute inset-x-0 top-full z-30 mt-1 max-h-52 overflow-y-auto rounded border border-gold-500 bg-cream-50 p-1 shadow-lg transition-[opacity,transform,visibility] duration-200 ease-out ${open ? "visible translate-y-0 opacity-100" : "pointer-events-none invisible -translate-y-1 opacity-0"}`}
      >
          {options.map((option, index) => {
            const isSelected = option === selected
            const isActive = index === activeIndex
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  setUncontrolledSelected(option)
                  onChange?.(option)
                  setActiveIndex(index)
                  setOpen(false)
                  buttonRef.current?.focus()
                }}
                className={`block w-full rounded px-2.5 py-2 text-left text-sm text-ink transition-colors duration-150 ease-out ${isActive || isSelected ? "bg-gold-400" : "hover:bg-cream-100"}`}
              >
                {option}
              </button>
            )
          })}
      </div>
    </div>
  )
}

export function SubmitButton({ loading, children, withArrow = true }: { loading: boolean; children: React.ReactNode; withArrow?: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="inline-flex items-center gap-3 rounded-full bg-forest-950 p-[10px] text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:opacity-60  sm:text-lg p-[10px]"
    >
      {loading ? "Sending..." : children}
      {withArrow && (
        <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-forest-950">
          <ArrowUpRight />
        </span>
      )}
    </button>
  )
}

export type FormStatus = "idle" | "loading" | "success" | "error"

/** Shared submit helper for the public forms. */
export function usePublicForm(endpoint: string) {
  const [status, setStatus] = useState<FormStatus>("idle")

  async function submit(event: React.FormEvent<HTMLFormElement>, payload: Record<string, unknown>): Promise<boolean> {
    event.preventDefault()
    if (status === "loading") return false
    setStatus("loading")
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`submit failed: ${res.status}`)
      setStatus("success")
      return true
    } catch {
      setStatus("error")
      return false
    }
  }

  function reset() {
    setStatus("idle")
  }

  return { status, submit, reset }
}

export function FormError() {
  return <p className="mt-3 text-sm font-medium text-red-700">Something went wrong — please try again.</p>
}

/** Card shell wrapping a form on a page. */
export function FormCard({
  title,
  subtitle,
  children,
  className = "",
}: {
  title: string
  subtitle: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`rounded-2xl bg-white p-5 shadow-sm sm:p-8 ${className}`}>
      <h3 className="text-lg font-semibold text-ink sm:text-xl">{title}</h3>
      <p className="mt-2 text-sm text-ink/70 sm:text-base">{subtitle}</p>
      <div className="mt-5 sm:mt-7">{children}</div>
    </div>
  )
}

/** Plain 24/600 section heading used inside forms (e.g. "Child details"). */
export function FormSectionHead({ children }: { children: React.ReactNode }) {
  return <h4 className="mb-3 mt-6 text-xl font-semibold text-ink first:mt-0 sm:mb-4 sm:mt-9 sm:text-2xl">{children}</h4>
}

export function SuccessCard({
  message,
  onReset,
  eyebrow = "Submission received",
  title = "Thank you for showing up.",
  resetLabel = "Submit another",
}: {
  message: string
  onReset: () => void
  eyebrow?: string
  title?: string
  resetLabel?: string
}) {
  return (
    <div className="success-card animate-fade-in-up">
      <div className="success-card__mark" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none">
          <path d="m6.5 12.5 3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <p className="success-card__eyebrow">{eyebrow}</p>
      <h1 className="success-card__title">{title}</h1>
      <p className="success-card__message">{message}</p>
      <div className="success-card__actions">
        <button type="button" onClick={onReset} className="success-card__primary">
          {resetLabel}
        </button>
        <Link to="/" className="success-card__secondary">Back to home</Link>
      </div>
    </div>
  )
}
